/**
 * Command generation and response parsing for firmware v4.0.
 *
 * Everything here is pure string work — no I/O. The transport layer (Phase 2)
 * takes the array from buildCommands() and writes it with COMMAND_DELAY_MS
 * between entries.
 */


import type { CycleSettings, Program, SessionState } from './types';
import { IDLE_SESSION } from './types';

/* ------------------------------------------------------------------ *
 * Building
 * ------------------------------------------------------------------ */

/**
 * Format a gate frequency for transmission.
 *
 * UNVERIFIED (Phase 0 task #5): the firmware ACKs as "OK GATE=10.00Hz", which
 * implies two-decimal support, but this has not been confirmed against the
 * source. If the firmware quantises to 0.5 Hz, values like 7.83 will silently
 * degrade. Until measured, we send the exact value and trim trailing zeros.
 */
export function formatGate(hz: number): string {
  return String(Number(hz.toFixed(2)));
}

/** The full command sequence for a program, ending in RUN. Spec §5 and §9. */
export function buildCommands(program: Program, options: { run?: boolean } = {}): string[] {
  const { run = true } = options;
  const cmds: string[] = ['PROG', `CYCLES ${program.cycles.length}`];

  program.cycles.forEach((c, i) => {
    cmds.push(`CYCLE ${i + 1}`);
    cmds.push(`ON ${c.onMin}`);
    cmds.push(`OFF ${c.offMin}`);
    cmds.push(`CARRIER ${Math.round(c.carrierHz)}`);
    cmds.push(`INTENSITY ${Math.round(c.intensity)}`);
    cmds.push(`GATEDUTY ${Math.round(c.gateDutyPct ?? 50)}`);
    if (c.sweepEnabled) {
      cmds.push(
        `SWEEP ${formatGate(c.sweepLo)} ${formatGate(c.sweepHi)} ${c.sweepHold} ${
          c.sweepTrue ? 'TRUE' : 'HALF'
        }`,
      );
    } else {
      cmds.push(`GATE ${formatGate(c.gateHz)}`);
    }
  });

  if (run) cmds.push('RUN');
  return cmds;
}

/** Save the current in-memory program to device flash under a name. */
export function buildSaveCommand(name: string): string {
  return `SAVE ${name}`;
}


/* ------------------------------------------------------------------ *
 * Descending ramps
 * ------------------------------------------------------------------ */

/**
 * Build a descending frequency ramp as chained cycles.
 *
 * The firmware's SWEEP enforces hi > lo, so it can only ramp upward. A
 * descending ramp has to be expressed as a series of fixed-gate cycles with
 * OFF 0 between them, which run back to back as one continuous block.
 *
 * @param from   starting (higher) frequency
 * @param to     ending (lower) frequency
 * @param steps  number of cycles to divide the ramp into (max 7 total per program)
 * @param minutesPerStep  ON duration for each step
 */
export function buildDescendingRamp(
  from: number,
  to: number,
  steps: number,
  minutesPerStep: number,
  template: Omit<CycleSettings, 'gateHz' | 'onMin' | 'offMin' | 'sweepEnabled'>,
): CycleSettings[] {
  if (steps < 1) return [];
  if (from <= to) throw new Error('Descending ramp requires from > to');

  const cycles: CycleSettings[] = [];
  // steps === 1 would divide by zero; a single step just sits at `from`.
  const delta = steps === 1 ? 0 : (from - to) / (steps - 1);

  for (let i = 0; i < steps; i++) {
    const hz = Number((from - delta * i).toFixed(2));
    cycles.push({
      ...template,
      sweepEnabled: false,
      gateHz: hz,
      onMin: minutesPerStep,
      // OFF 0 chains this cycle straight into the next. The final cycle's OFF
      // is ignored by the firmware anyway.
      offMin: 0,
    });
  }
  return cycles;
}

/* ------------------------------------------------------------------ *
 * Parsing
 * ------------------------------------------------------------------ */

export type ParsedResponse =
  | { kind: 'welcome'; firmware: string; autorun: boolean; saved: string[] }
  | { kind: 'status'; state: SessionState }
  | { kind: 'ack'; text: string }
  | { kind: 'error'; text: string }
  | { kind: 'sweep'; hz: number; direction: 'up' | 'down' | 'held' }
  | { kind: 'phase'; phase: 'TREATMENT' | 'REST'; cycle?: number; totalCycles?: number }
  | { kind: 'complete'; stopped: boolean }
  | { kind: 'list'; saved: string[] }
  /** Header line of a SHOW response — marks the start of a new dump. */
  | { kind: 'show-header'; cycleCount: number }
  /** One CYCLE line of a SHOW response. */
  | { kind: 'show-cycle'; index: number; cycle: CycleSettings }
  | { kind: 'unknown'; text: string };

const num = (s: string | undefined): number => (s === undefined ? 0 : Number(s));

/**
 * Parse one TX notification.
 *
 * Responses are human-readable text (spec §4), so this is string matching
 * rather than binary decoding. Order matters: more specific patterns first.
 */
export function parseResponse(raw: string): ParsedResponse {
  const text = raw.trim();
  const upper = text.toUpperCase();

  if (upper.startsWith('ERR')) {
    return { kind: 'error', text: text.replace(/^ERR\s*/i, '') };
  }

  // Greeting. The firmware sends:
  //   "PEMF-Controller v4.0 | AUTORUN=OFF | saved: A, B | send PROG to configure..."
  // There is NO "ready" token — the spec document has one, the device does not.
  // Requiring it meant the greeting never parsed and firmware version, AUTORUN
  // state and the saved-program list never populated on connect.
  if (upper.includes('PEMF-CONTROLLER')) {
    const fw = /PEMF-Controller\s+(v[\d.]+)/i.exec(text)?.[1] ?? 'unknown';
    const autorun = /AUTORUN\s*=\s*ON/i.test(text);
    const savedPart = /saved:\s*([^|]*)/i.exec(text)?.[1] ?? '';
    return { kind: 'welcome', firmware: fw, autorun, saved: splitNames(savedPart) };
  }

  // Reconnecting mid-session: "Phone reconnected | session running | send STATUS".
  // This must be caught BEFORE the RUNNING check below, or the substring
  // "running" matches, parseStatus finds no fields, and the live session
  // display is overwritten with zeros — corrupting exactly the state that
  // reconnecting exists to restore.
  if (upper.includes('PHONE RECONNECTED')) {
    return { kind: 'ack', text };
  }

  if (upper.startsWith('SAVED PROGRAMS:')) {
    return { kind: 'list', saved: splitNames(text.slice(text.indexOf(':') + 1)) };
  }

  // SHOW output: a header line then one line per cycle. Parsed here so LOAD can
  // actually populate the editor — this wiring was missing until 2026-07-19.
  const showHeader = /CURRENT PROGRAM\s*\|\s*(\d+)\s*cycle/i.exec(text);
  if (showHeader) {
    return { kind: 'show-header', cycleCount: num(showHeader[1]) };
  }
  const showCycle = parseShowLine(text);
  if (showCycle) {
    return { kind: 'show-cycle', index: showCycle.index, cycle: showCycle.cycle };
  }

  if (upper.includes('SESSION COMPLETE')) return { kind: 'complete', stopped: false };
  if (upper.includes('SESSION STOPPED') || upper === 'STOPPED') {
    return { kind: 'complete', stopped: true };
  }

  // "SWEEP 10.5Hz up" / "SWEEP 10.5Hz down" / "SWEEP 12.0Hz (held)"
  const sweep = /SWEEP\s+([\d.]+)\s*HZ\s*(.*)$/i.exec(text);
  if (sweep) {
    const tail = (sweep[2] ?? '').toLowerCase();
    const direction = tail.includes('held')
      ? 'held'
      : tail.includes('↓') || tail.includes('down')
        ? 'down'
        : 'up';
    return { kind: 'sweep', hz: num(sweep[1]), direction };
  }

  if (upper.startsWith('IDLE')) {
    return { kind: 'status', state: { ...IDLE_SESSION } };
  }

  if (upper.includes('RUNNING')) {
    return { kind: 'status', state: parseStatus(text) };
  }

  // Live phase transitions: "TREATMENT 2/4 | ..." or "REST | 90 min | ..."
  const treat = /^TREATMENT\s+(\d+)\s*\/\s*(\d+)/i.exec(text);
  if (treat) {
    return {
      kind: 'phase',
      phase: 'TREATMENT',
      cycle: num(treat[1]),
      totalCycles: num(treat[2]),
    };
  }
  if (upper.startsWith('REST')) return { kind: 'phase', phase: 'REST' };

  if (upper.startsWith('OK') || upper.startsWith('PROG RESET') || upper.startsWith('RUN START')) {
    return { kind: 'ack', text };
  }

  return { kind: 'unknown', text };
}

/** Parse a running STATUS line into session state. Spec §4.2. */
export function parseStatus(text: string): SessionState {
  const cycle = /cycle\s+(\d+)\s*\/\s*(\d+)/i.exec(text);
  const timeLeft = /(\d+)\s*min\s+(?:left|until next)/i.exec(text);
  const total = /~\s*(\d+)\s*min\s+total/i.exec(text);
  const sweepFreq = /sweep@\s*([\d.]+)\s*Hz/i.exec(text);
  const gateFreq = /gate\s*=\s*([\d.]+)\s*Hz/i.exec(text);

  const phase: SessionState['phase'] = /REST/i.test(text)
    ? 'REST'
    : /TREATMENT/i.test(text)
      ? 'TREATMENT'
      : 'IDLE';

  return {
    running: /RUNNING/i.test(text),
    phase,
    cycle: num(cycle?.[1]),
    totalCycles: num(cycle?.[2]),
    timeLeft: num(timeLeft?.[1]),
    freq: num(sweepFreq?.[1] ?? gateFreq?.[1]),
    totalRemaining: num(total?.[1]),
  };
}

/** Parse one CYCLE line from a SHOW response back into settings. Spec §4.6. */
export function parseShowLine(text: string): { index: number; cycle: CycleSettings } | null {
  const head = /^CYCLE\s+(\d+):\s*(\d+)min\s+ON\s+(\d+)min\s+OFF/i.exec(text);
  if (!head) return null;

  const carrier = /carrier\s*=\s*([\d.]+)\s*Hz/i.exec(text);
  const intensity = /intensity\s*=\s*(\d+)\s*%/i.exec(text);
  const gate = /gate\s*=\s*([\d.]+)\s*Hz/i.exec(text);
  const duty = /duty\s*=\s*(\d+)\s*%/i.exec(text);
  const sweep = /sweep\s+([\d.]+)\s*-\s*([\d.]+)\s*Hz\s+hold\s*=\s*(\d+)s\s+(TRUE|HALF)/i.exec(text);

  return {
    index: num(head[1]) - 1,
    cycle: {
      onMin: num(head[2]),
      offMin: num(head[3]),
      carrierHz: num(carrier?.[1]) || 1000,
      intensity: num(intensity?.[1]),
      sweepEnabled: Boolean(sweep),
      gateHz: num(gate?.[1]) || 10,
      sweepLo: num(sweep?.[1]) || 8,
      sweepHi: num(sweep?.[2]) || 12,
      sweepHold: num(sweep?.[3]) || 10,
      sweepTrue: (sweep?.[4] ?? 'TRUE').toUpperCase() === 'TRUE',
      gateDutyPct: num(duty?.[1]) || 50,
    },
  };
}

function splitNames(s: string): string[] {
  return s
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n.length > 0 && !/^none$/i.test(n));
}
